import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { useStore } from '../../lib/hooks/useStore';
import { SupportedDevices } from '../../lib/typegen/supported-devices';
import LedBoard, { LedBoardStub } from '../../lib/devices/led-board/LedBoard';
import LedBoardDevice, { LedChannel } from '../../lib/devices/led-board/LedBoardDevice';
import {
	isBarcodeScannerAcceptingScans,
	isNeedsAttentionState,
	resolveActiveDeviceMapping,
	resolveBaseChannelPattern,
} from '../../lib/devices/led-board/ledPatternResolver';
import pageIds from '../../lib/constants/page-ids';

const BRANDING_CYCLE_MS = 5000;
const COMPONENT_BLINK_MS = 500;
const BASE_CHANNEL_OFF_COLOR = '000000';

const LedIndicator = observer(function LedIndicator() {
	const {
		kioskStore: { isDeviceSupported, lastKioskStateOnServer, useGlobalBarcodeScanner },
		sessionStore: { isActive },
		configStore: { ledBoardConfig, envDeviceOption, currentPage, currentPageId, pages, pageParams },
	} = useStore();

	const [ledBoard, setLedBoard] = useState<LedBoardDevice | null>(null);
	const brandingIndexRef = useRef(0);
	const brandingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const componentBlinkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	useEffect(() => {
		if (!isDeviceSupported(SupportedDevices.LedControlBoard) || !ledBoardConfig) {
			setLedBoard(null);
			return;
		}
		setLedBoard(envDeviceOption === 'never' ? new LedBoardStub(ledBoardConfig.deviceName) : new LedBoard(ledBoardConfig.deviceName));
	}, [isDeviceSupported, ledBoardConfig, envDeviceOption]);

	useEffect(() => {
		if (brandingIntervalRef.current) {
			clearInterval(brandingIntervalRef.current);
			brandingIntervalRef.current = null;
		}
		if (!ledBoard || !ledBoardConfig) return;

		const channel = ledBoardConfig.baseChannel as LedChannel;
		const attention = isNeedsAttentionState(lastKioskStateOnServer);
		const shouldCycle = !attention && !isActive && ledBoardConfig.brandingColors.length > 1;
		if (!shouldCycle) brandingIndexRef.current = 0;

		const applyPattern = () => {
			const pattern = resolveBaseChannelPattern({
				channel,
				kioskState: lastKioskStateOnServer,
				sessionActive: isActive,
				brandingColors: ledBoardConfig.brandingColors,
				brandingColorIndex: brandingIndexRef.current,
			});
			if (!pattern) return;
			ledBoard.setAllColor(pattern.channel, pattern.color);
		};

		applyPattern();

		if (shouldCycle) {
			brandingIntervalRef.current = setInterval(() => {
				brandingIndexRef.current += 1;
				applyPattern();
			}, BRANDING_CYCLE_MS);
		}

		return () => {
			if (brandingIntervalRef.current) {
				clearInterval(brandingIntervalRef.current);
				brandingIntervalRef.current = null;
			}
			ledBoard.setAllColor(channel, BASE_CHANNEL_OFF_COLOR);
		};
	}, [ledBoard, ledBoardConfig, lastKioskStateOnServer, isActive]);

	useEffect(() => {
		const stopBlinking = (outputNumber?: number) => {
			if (componentBlinkIntervalRef.current) {
				clearInterval(componentBlinkIntervalRef.current);
				componentBlinkIntervalRef.current = null;
			}
			if (outputNumber !== undefined) ledBoard?.setOutputOff(outputNumber);
		};

		if (!ledBoard || !ledBoardConfig) {
			stopBlinking();
			return;
		}

		const componentTypesOnPage = currentPage?.components?.map((component) => component.type) ?? [];
		const mapping = resolveActiveDeviceMapping({
			deviceMappings: ledBoardConfig.deviceMappings,
			componentTypesOnPage,
			sessionActive: isActive,
			barcodeScannerAccepting: isBarcodeScannerAcceptingScans({
				pageIds: pages.map((page) => page.id),
				currentPageId,
				lookupAccountPageId: pageIds.lookupAccount,
				useGlobalBarcodeScanner,
				kioskNeedsAttention: isNeedsAttentionState(lastKioskStateOnServer),
				pageParams,
			}),
		});

		if (!mapping) {
			stopBlinking();
			return;
		}

		let isOn = false;
		componentBlinkIntervalRef.current = setInterval(() => {
			isOn = !isOn;
			if (isOn) ledBoard.setOutputOn(mapping.outputNumber);
			else ledBoard.setOutputOff(mapping.outputNumber);
		}, COMPONENT_BLINK_MS);

		return () => stopBlinking(mapping.outputNumber);
	}, [ledBoard, ledBoardConfig, isActive, currentPage, currentPageId, pages, pageParams, useGlobalBarcodeScanner, lastKioskStateOnServer]);

	return <></>;
});

export default LedIndicator;
